import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'admin/template/industry/socialCategories/templates/<systemTemplateId>';

interface ISocialTemplateWithStatuses {
	statuses: Api.ITemplateStatus[];
	template: Api.ITemplate;
}

function socialTemplateQueryOptions(userSession: Api.UserSessionContext, systemTemplateId: string) {
	return {
		queryFn: () =>
			userSession.webServiceHelper.callAsync<ISocialTemplateWithStatuses>(
				`admin/template/industry/socialCategories/templates/${encodeURIComponent(systemTemplateId)}`,
				'GET'
			),
		queryKey: [BASE_QUERY_KEY, systemTemplateId],
	};
}

export function useAdminSocialTemplateImagesQuery({
	enabled = true,
	systemTemplateId,
}: {
	enabled?: boolean;
	systemTemplateId?: string;
}) {
	const userSession = useUserSession();
	return useQuery({
		...socialTemplateQueryOptions(userSession, systemTemplateId),
		enabled: enabled && !!systemTemplateId,
		// A linked post stores its deck's slides as images, exported in slide order.
		select: result =>
			(result?.template?.attachments ?? []).map(attachment => (attachment as Api.IFileAttachmentWithURL).url),
		meta: {
			errorLogging: { category: 'ImageTemplate', action: 'AdminSocialTemplateImagesQuery-Error' },
		},
	});
}

// Re-reads the template through the same cache entry, so anything showing its images updates from this one request.
export const refetchAdminSocialTemplate = (userSession: Api.UserSessionContext, systemTemplateId: string) =>
	reactQueryClient.fetchQuery({ ...socialTemplateQueryOptions(userSession, systemTemplateId), staleTime: 0 });
